import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { Accent, DisplayFont, ThemeName, ThemeSelection } from './theme'
import { loadSelection, saveSelection, themeMode, themeVars } from './theme'

interface ThemeContextValue extends ThemeSelection {
  setTheme: (t: ThemeName) => void
  setAccent: (a: Accent) => void
  setFont: (f: DisplayFont) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [sel, setSel] = useState<ThemeSelection>(() => loadSelection())

  // The variables go on <html> rather than a wrapper div so that anything
  // portalled out of the tree — a modal, a toast — still resolves --t-*.
  useEffect(() => {
    const root = document.documentElement
    const vars = themeVars(sel)
    for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v)
    root.dataset.tesoraTheme = sel.theme
    // Exposed so CSS can adapt where a token cannot carry the difference — a
    // shadow that must darken on light and lighten on dark, for instance.
    root.dataset.tesoraMode = themeMode(sel.theme)
    saveSelection(sel)
  }, [sel])

  const value = useMemo<ThemeContextValue>(
    () => ({
      ...sel,
      setTheme: (theme) => setSel((s) => ({ ...s, theme })),
      setAccent: (accent) => setSel((s) => ({ ...s, accent })),
      setFont: (font) => setSel((s) => ({ ...s, font })),
    }),
    [sel],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (ctx === null) throw new Error('useTheme must be used inside a ThemeProvider')
  return ctx
}

/** Convenience for components that only need to set one axis. */
// eslint-disable-next-line react-refresh/only-export-components
export function useSetAccent(): (a: Accent) => void {
  const { setAccent } = useTheme()
  return useCallback((a: Accent) => setAccent(a), [setAccent])
}
